import React, { createContext, useContext, useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import Dashboard from './pages/Dashboard'
import InitiativeDetails from './pages/InitiativeDetails'
import TeamWorkloadDetailView from './pages/TeamWorkloadDetailView'
import TestPage from './pages/TestPage'
import ReleasePage from './pages/ReleasePage'

// Import authentication components
import { OktaAuthProvider, useOktaAuth } from './contexts/OktaAuthContext'
import ProtectedRoute from './components/auth/ProtectedRoute'
import LoginCallback from './components/auth/LoginCallback'
import AuthErrorBoundary from './components/auth/AuthErrorBoundary'
import { setTokenGetter } from './services/apiService'

// Import global tooltip style overrides
import './styles/tooltipOverrides.css'

// Import debug utilities in development
if (process.env.NODE_ENV === 'development') {
  import('./utils/teamDebugUtils')
}

// Theme Context
interface ThemeContextType {
  darkMode: boolean
  toggleDarkMode: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Create themes
const createAppTheme = (darkMode: boolean) => createTheme({
  palette: {
    mode: darkMode ? 'dark' : 'light',
    primary: {
      main: '#3498db',
      dark: '#2980b9',
    },
    secondary: {
      main: darkMode ? '#34495e' : '#2c3e50',
    },
    background: {
      default: darkMode ? '#121212' : '#f8f9fa',
      paper: darkMode ? '#1e1e1e' : '#ffffff',
    },
    text: {
      primary: darkMode ? '#ffffff' : '#2c3e50',
      secondary: darkMode ? '#b0b0b0' : '#6c757d',
    },
  },
  typography: {
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: darkMode
            ? '0 2px 8px rgba(0,0,0,0.3)'
            : '0 2px 8px rgba(0,0,0,0.1)',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: darkMode
            ? '0 2px 8px rgba(0,0,0,0.3)'
            : '0 2px 8px rgba(0,0,0,0.1)',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-outlined': {
            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
            color: darkMode ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)',
            backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'transparent',
          },
          '&.MuiChip-colorSecondary.MuiChip-outlined': {
            color: darkMode ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)',
            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.23)',
            backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'transparent',
          },
          '&.MuiChip-colorPrimary.MuiChip-outlined': {
            color: darkMode ? '#90caf9' : '#1976d2',
            borderColor: darkMode ? '#90caf9' : '#1976d2',
            backgroundColor: darkMode ? 'rgba(144,202,249,0.1)' : 'transparent',
          },
          '&.MuiChip-colorInfo.MuiChip-outlined': {
            color: darkMode ? '#81d4fa' : '#0288d1',
            borderColor: darkMode ? '#81d4fa' : '#0288d1',
            backgroundColor: darkMode ? 'rgba(129,212,250,0.1)' : 'transparent',
          },
          '&.MuiChip-colorDefault.MuiChip-outlined': {
            color: darkMode ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)',
            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
            backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'transparent',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
            },
            '&:hover fieldset': {
              borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.87)',
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '&.MuiToolbar-dense': {
            minHeight: 48, // Consistent compact height
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: darkMode
            ? '0 2px 4px rgba(0,0,0,0.3)'
            : '0 2px 4px rgba(0,0,0,0.1)', // Subtle shadow for better separation
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
          // Firefox scrollbar
          scrollbarWidth: 'thin',
          scrollbarColor: darkMode ? '#555555 #2a2a2a' : '#c1c1c1 #f1f1f1',
        },
        // Scrollbar styling for WebKit browsers
        '*::-webkit-scrollbar': {
          width: '12px',
          height: '12px',
        },
        '*::-webkit-scrollbar-track': {
          background: darkMode ? '#2a2a2a' : '#f1f1f1',
          borderRadius: '6px',
        },
        '*::-webkit-scrollbar-thumb': {
          background: darkMode ? '#555555' : '#c1c1c1',
          borderRadius: '6px',
          border: darkMode ? '2px solid #2a2a2a' : '2px solid #f1f1f1',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            background: darkMode ? '#666666' : '#a8a8a8',
          },
          '&:active': {
            background: darkMode ? '#777777' : '#999999',
          },
        },
        '*::-webkit-scrollbar-corner': {
          background: darkMode ? '#2a2a2a' : '#f1f1f1',
        },
      },
    },
  },
})

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem('darkMode')
    if (savedTheme !== null) {
      return JSON.parse(savedTheme)
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  const toggleDarkMode = () => {
    setDarkMode((prev: boolean) => {
      const newMode = !prev
      localStorage.setItem('darkMode', JSON.stringify(newMode))
      return newMode
    })
  }

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      if (localStorage.getItem('darkMode') === null) {
        setDarkMode(e.matches)
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Update body data attribute for dark mode scrollbar styling
  useEffect(() => {
    if (darkMode) {
      document.body.setAttribute('data-theme', 'dark')
      document.documentElement.setAttribute('data-mui-color-scheme', 'dark')
    } else {
      document.body.removeAttribute('data-theme')
      document.documentElement.removeAttribute('data-mui-color-scheme')
    }
  }, [darkMode])

  const theme = createAppTheme(darkMode)

  return (
    <AuthErrorBoundary>
      <OktaAuthProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <AppWithAuth />
            </ThemeProvider>
          </ThemeContext.Provider>
        </QueryClientProvider>
      </OktaAuthProvider>
    </AuthErrorBoundary>
  )
}

// Component that handles authentication setup and routing
const AppWithAuth: React.FC = () => {
  const { getAccessToken } = useOktaAuth()

  // Set up the token getter for the API service
  useEffect(() => {
    setTokenGetter(getAccessToken)
  }, [getAccessToken])

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Public route for login callback */}
        <Route path="/login/callback" element={<LoginCallback />} />

        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/test" element={
          <ProtectedRoute>
            <TestPage />
          </ProtectedRoute>
        } />
        <Route path="/releases" element={
          <ProtectedRoute>
            <ReleasePage />
          </ProtectedRoute>
        } />
        <Route path="/initiative/:initiativeKey" element={
          <ProtectedRoute>
            <InitiativeDetails />
          </ProtectedRoute>
        } />
        <Route path="/team-workload" element={
          <ProtectedRoute>
            <TeamWorkloadDetailView />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  )
}

export default App
